/**
 * Сигнали сесії між вкладками (UX-24, UX-30; E3a-06b): `BroadcastChannel` того самого походження.
 * - `session-expired` — вкладка отримала `401 session_expired`: cookie спільна, тож сесія спливла
 *   й для решти вкладок; вони ховають вміст і показують повторний вхід (`platform/relogin.ts`).
 * - `signed-in` — у якійсь вкладці щойно ввійшли (сторінка входу чи шар поверх сторінки). Сигнал
 *   нічого не доводить і особи не несе: кожна вкладка сама питає `GET /api/v1/me`, хто тепер у
 *   cookie, і вирішує за відповіддю сервера.
 *
 * Модуль крихітний і без залежностей: його бере й документ входу (бюджет ≤ 100 КБ).
 * Вихід (`sign-out`) іде своїм каналом чернеток і виходу (`ui/save/drafts.ts`).
 */
export const SESSION_SIGNAL_CHANNEL = 'clientline.session-signal';

export type SessionSignal = 'session-expired' | 'signed-in';

const SIGNALS: ReadonlySet<string> = new Set<SessionSignal>(['session-expired', 'signed-in']);

/**
 * Один екземпляр каналу на документ: свого ж повідомлення він не отримує, тож вкладка, що
 * надіслала сигнал, не реагує на нього вдруге.
 */
let channel: BroadcastChannel | null | undefined;

function shared(): BroadcastChannel | null {
  if (channel === undefined) {
    channel =
      typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SESSION_SIGNAL_CHANNEL);
    // Node (тести) інакше тримав би процес живим заради відкритого каналу; у браузері методу немає.
    (channel as { unref?: () => void } | null)?.unref?.();
  }
  return channel;
}

export function sendSessionSignal(type: SessionSignal): void {
  try {
    shared()?.postMessage({ type });
  } catch {
    // Канал закрито чи недоступний — інші вкладки дізнаються з власної відповіді сервера.
  }
}

export function onSessionSignal(listener: (type: SessionSignal) => void): () => void {
  const ch = shared();
  if (!ch) return () => undefined;
  const handler = (event: MessageEvent<{ type?: unknown }>) => {
    const type = event.data?.type;
    if (typeof type === 'string' && SIGNALS.has(type)) listener(type as SessionSignal);
  };
  ch.addEventListener('message', handler);
  return () => ch.removeEventListener('message', handler);
}

/** Лише для тестів: новий канал, без слухачів минулого тесту. */
export function resetSessionSignalForTests(): void {
  channel?.close();
  channel = undefined;
}
